import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, Eye, Heart, Globe2, Lightbulb, Users, Leaf as LeafIcon,
  ArrowRight
} from 'lucide-react';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AboutPage = () => {
  const { locale } = useParams();

  const values = [
    { icon: Heart, title: 'Integrity', description: 'Transparent, ethical conduct in every engagement' },
    { icon: Target, title: 'Excellence', description: 'Uncompromising quality in deliverables' },
    { icon: Globe2, title: 'Global Mindset', description: 'Local expertise with international standards' },
    { icon: Lightbulb, title: 'Innovation', description: 'Forward-thinking solutions for complex challenges' },
    { icon: Users, title: 'Client-First', description: 'Your success drives every decision we make' },
    { icon: LeafIcon, title: 'Sustainability', description: 'Building a better future through responsible advisory' },
  ];

  const offices = [
    { city: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱', address: 'De Entree 232, 1101EE' },
    { city: 'Nashik', country: 'India', flag: '🇮🇳', address: 'Tidke Colony, Nashik 422002' },
    { city: 'Pune', country: 'India', flag: '🇮🇳', address: 'Baner, Pune 411045' },
  ];

  const leadership = [
    {
      name: 'Raju Wagh',
      title: 'Executive Director',
      credentials: 'B.E. Mechanical Engineer · Chartered Engineer · Electronics & Electrical Certification',
      image: '/images/external/photo-1554224155-8d04cb21cd6c.jpg',
      bio: [
        'With over 27 years of experience in pharmaceutical engineering, maintenance, utilities and project management, Raju brings a strong combination of technical expertise, hands-on plant experience and a practical approach to engineering challenges.',
        'His professional experience covers engineering management, maintenance excellence, greenfield and brownfield projects, critical utility systems, HVAC, clean utilities, energy optimization, engineering documentation, and regulatory and statutory compliance across pharmaceutical formulation and API manufacturing facilities.',
        'With a strong understanding of actual plant operations, he focuses on developing practical, reliable and cost-effective engineering solutions that improve equipment reliability, operational efficiency, energy performance and regulatory compliance.',
        'At H&G, his expertise is particularly focused on engineering strategy, technical due diligence, maintenance improvement, utility optimization, compliance readiness and engineering solutions that deliver sustainable long-term plant performance.',
      ],
    },
    {
      name: 'Nitin S. Kshirsagar',
      title: 'Pharma Engineering & Project Execution Lead',
      credentials: 'DME Engineer · Pharma Engineering & Project Management Professional',
      image: '/images/external/photo-1532187863486-abf9dbad1b69.jpg',
      bio: [
        'With over 30 years of experience in pharmaceutical engineering and turnkey project execution, Nitin brings extensive expertise in translating project concepts and engineering requirements into successfully executed pharmaceutical facilities.',
        'His professional experience spans turnkey project management, greenfield and brownfield facility execution, project planning and coordination, engineering procurement, contractor management, installation, commissioning and project handover, with a strong focus on pharmaceutical formulation manufacturing facilities.',
        'Having worked closely with multiple project stakeholders, he brings a practical understanding of site execution, project coordination, timelines, resources, quality requirements and execution challenges involved in pharmaceutical projects.',
        'At H&G, his expertise is focused on end-to-end project execution, turnkey implementation, project coordination and delivery, helping clients convert their engineering plans into efficient, compliant and operational pharmaceutical facilities.',
      ],
    },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      <SEO 
        title="About H&G"
        description="H&G (Horizon & Greenprint) is a global partner serving Fortune 500 organizations across engineering, sustainability, finance, capital markets, and M&A advisory — and the products and services that drive the next decade of growth."
        path="/about"
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mb-6">
              We Are H&G
            </h1>
            <p className="text-lg text-mid-grey">
              Horizon & Greenprint — where strategic vision meets engineering precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Our Story
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4 mb-8">
              Building Excellence Across Borders
            </motion.h2>
            
            <motion.div variants={fadeInUp} className="prose prose-lg text-mid-grey">
              <p className="mb-6">
                H&G was founded on a simple premise: that the world&rsquo;s most complex business challenges 
                require advisors who understand both the strategic horizon and the operational blueprint. 
                Our name — Horizon & Greenprint — reflects this dual commitment.
              </p>
              <p className="mb-6">
                Today, we serve Fortune 500 organizations across India and the Netherlands — delivering advisory,
                engineering, and execution across pharmaceutical engineering, sustainability, financial 
                structuring, capital markets, and M&A. New service lines and products are on the way.
              </p>
              <p>
                What sets us apart is our refusal to choose between breadth and depth. Each of our 
                practice areas is led by industry veterans with 20+ years of hands-on experience, 
                supported by rigorous methodologies refined through hundreds of engagements.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border-l-4 border-teal"
            >
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-teal" />
              </div>
              <h3 className="font-playfair text-2xl text-navy mb-4">Our Mission</h3>
              <p className="text-mid-grey leading-relaxed italic">
                &ldquo;To deliver transformative advisory that creates lasting value at the intersection 
                of engineering excellence, regulatory expertise, and strategic capital.&rdquo;
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border-l-4 border-teal"
            >
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-teal" />
              </div>
              <h3 className="font-playfair text-2xl text-navy mb-4">Our Vision</h3>
              <p className="text-mid-grey leading-relaxed italic">
                &ldquo;To be the most trusted multi-disciplinary partner — across services and products — 
                for Fortune 500 organizations across Asia, the Middle East, Europe, and the Americas.&rdquo;
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              What Guides Us
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Our Values
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  variants={fadeInUp}
                  className="value-card text-center p-8 bg-light-grey rounded-xl"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <IconComponent className="w-8 h-8 text-teal" />
                  </div>
                  <h4 className="font-semibold text-navy text-lg mb-2">{value.title}</h4>
                  <p className="text-mid-grey text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 md:py-28 bg-light-grey" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              People Behind H&G
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Senior Leadership
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-mid-grey mt-4 max-w-2xl mx-auto">
              Industry veterans with decades of hands-on Fortune 500 experience across engineering, compliance, and project execution.
            </motion.p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {leadership.map((person) => (
              <motion.div
                key={person.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`leadership-card-${person.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="grid sm:grid-cols-3 gap-0">
                  {/* Image column — decorative pharma-facility image (not a portrait) */}
                  <div className="sm:col-span-1 h-48 sm:h-auto overflow-hidden bg-navy relative">
                    <img
                      src={person.image}
                      alt={`${person.name} — ${person.title}`}
                      className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-navy/40 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-navy font-playfair font-bold text-lg shadow-lg">
                        {person.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                      </div>
                    </div>
                  </div>

                  {/* Content column */}
                  <div className="sm:col-span-2 p-6 md:p-8">
                    <h3 className="font-playfair text-2xl text-navy">{person.name}</h3>
                    <p className="text-teal font-medium text-sm mt-1">{person.title}</p>
                    <p className="text-mid-grey text-xs uppercase tracking-wider mt-1">{person.credentials}</p>

                    <div className="mt-4 space-y-3">
                      {person.bio.map((paragraph, i) => (
                        <p key={i} className="text-mid-grey text-sm leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Footprint */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Our Presence
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Global Footprint
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 gap-6"
          >
            {offices.map((office) => (
              <motion.div
                key={office.city}
                variants={fadeInUp}
                className="office-card bg-light-grey p-6 rounded-xl text-center border-2 border-transparent hover:border-teal transition-colors"
              >
                <span className="text-3xl mb-3 block">{office.flag}</span>
                <h4 className="font-semibold text-navy">{office.city}</h4>
                <p className="text-mid-grey text-sm">{office.country}</p>
                <p className="text-xs text-mid-grey mt-2">{office.address}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Let&rsquo;s discuss how H&G can support your next strategic initiative.
          </p>
          <Link to={`/${locale}/contact`} className="btn-primary inline-flex items-center">
            Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
